import React from 'react';
import { getAuth, multiFactor } from 'firebase/auth';
import { Navigate, redirect } from 'react-router';

import { LoginPage, isUserEnrolledInMFAMethod, MFA_FACTOR_IDS } from '../../features/authentication';
import { getUserRoleFromAuthToken } from '../../utils/authHelpers';
import { isPatient } from '../../utils';
import { TOTPSetupPage, SMSSetupPage } from '../../features/authentication/mfa';
import { SharedBaseLayout } from '../layouts';
import { authenticationRoutes, baseFormsRoutes } from './base-routes';
import { getRequestedMFAFactorId } from './getRequestedMFAFactorId.ts';

/**
 * @type {import('react-router').LoaderFunction}
 */
const mfaSetupLoader = async ({ request }) => {
  const user = getAuth().currentUser;
  const url = new URL(request.url);
  const requestedFactorId = getRequestedMFAFactorId(url.pathname);

  if (!user) {
    const redirectTo = url.pathname;

    throw redirect(`/login?redirect=${redirectTo}`);
  }

  if (!requestedFactorId) {
    throw redirect('/dashboard');
  }

  // Revalidate and re-read claims so route access decisions use current auth state.
  const tokenResult = await user.getIdTokenResult(true);
  const userType = getUserRoleFromAuthToken(tokenResult.claims);

  const multiFactorUser = multiFactor(user);
  const isAlreadyEnrolledInRequestedMethod = isUserEnrolledInMFAMethod({
    factorId: requestedFactorId,
    multiFactorUser,
  });

  const isSMSSetupRoute = requestedFactorId === MFA_FACTOR_IDS.PHONE;
  const isBlockedSMSSetup = isSMSSetupRoute && !isPatient(userType);

  if (isAlreadyEnrolledInRequestedMethod || isBlockedSMSSetup) {
    throw redirect('/dashboard');
  }

  return {
    user,
  };
};

/**
 * These are the routes shared between all users:
 * - Unauthenticated users
 * - Authenticated users
 *   - Verified users
 *   - Unverified users
 * @satisfies {ExtendedRouteObject[]}
 */
export const sharedRoutes = [
  ...authenticationRoutes,
  {
    name: 'MFA Setup',
    path: 'mfa-setup',
    type: 'parent',
    children: [
      {
        name: 'Setup MFA Index',
        path: '',
        index: true,
        type: 'item',
        element: <Navigate replace to="dashboard" />,
      },
      {
        name: 'Setup TOTP MFA',
        path: /** @satisfies {FactorId} */ ('totp'),
        type: 'item',
        loader: mfaSetupLoader,
        element: <TOTPSetupPage />,
      },
      {
        name: 'Setup SMS MFA',
        path: /** @satisfies {FactorId} */ ('phone'),
        type: 'item',
        loader: mfaSetupLoader,
        element: <SMSSetupPage />,
      },
    ],
  },
  {
    name: 'Shared Layout With Authentication Layout',
    type: 'layout',
    element: <SharedBaseLayout />,
    children: [...baseFormsRoutes],
  },
  {
    name: 'Reauthenticate',
    path: 'reauthenticate',
    type: 'item',
    element: <LoginPage noPaper reauthenticate />,
  },
];
